import { useEffect, useMemo, useState } from 'react'
import { LIST_COLUMNS_SETTINGS } from '../data/listColumnsSettings'
import { getPreference, savePreference } from '../api/preferences'

type Props = {
  open: boolean
  onClose: () => void
  onSaved?: () => void
}

export function ColumnsSettingsModal({ open, onClose, onSaved }: Props) {
  const rows = LIST_COLUMNS_SETTINGS.rows
  const statusOptions = LIST_COLUMNS_SETTINGS.statusOptions

  const initialChecked = useMemo(() => {
    const map: Record<string, boolean> = {}
    for (const r of rows) map[r.value] = r.checked
    return map
  }, [])

  const initialPriority = useMemo(() => {
    const map: Record<string, string> = {}
    for (const r of rows) map[r.value] = r.priority
    return map
  }, [])

  const [checked, setChecked] = useState(initialChecked)
  const [priority, setPriority] = useState(initialPriority)
  const [statusFilter, setStatusFilter] = useState<string>(LIST_COLUMNS_SETTINGS.statusFilter)
  const [msg, setMsg] = useState('')

  useEffect(() => {
    if (!open) return
    void getPreference<{ checked: Record<string, boolean>; priority: Record<string, string>; statusFilter: string }>('clients.columns')
      .then((saved) => {
        if (!saved) return
        setChecked(saved.checked)
        setPriority(saved.priority)
        setStatusFilter(saved.statusFilter)
      })
  }, [open])

  if (!open) return null

  const selectedCount = Object.values(checked).filter(Boolean).length

  const onSave = async () => {
    if (selectedCount === 0) {
      setMsg('Please select at least one column.')
      return
    }
    await savePreference('clients.columns', { checked, priority, statusFilter })
    onSaved?.()
    setMsg('Columns Settings updated successfully.')
    setTimeout(() => {
      setMsg('')
      onClose()
    }, 700)
  }

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="columns-settings-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-panel__head">
          <h2 id="columns-settings-title">Client Columns Settings</h2>
          <button type="button" className="icon-btn" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-panel__body">
          <section className="fs-print-section">
            <h3>Columns Settings</h3>
            <table className="fs-print-table">
              <thead>
                <tr>
                  <th />
                  <th>Column name</th>
                  <th>Priority</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.value}>
                    <td>
                      <input
                        type="checkbox"
                        checked={!!checked[r.value]}
                        onChange={(e) =>
                          setChecked((prev) => ({
                            ...prev,
                            [r.value]: (e.target as HTMLInputElement).checked,
                          }))
                        }
                      />
                    </td>
                    <td>{r.label}</td>
                    <td>
                      <input
                        className="fs-priority-input"
                        type="text"
                        value={priority[r.value] || ''}
                        disabled={!!r.priorityDisabled && !checked[r.value]}
                        onChange={(e) =>
                          setPriority((prev) => ({
                            ...prev,
                            [r.value]: (e.target as HTMLInputElement).value,
                          }))
                        }
                      />
                    </td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={2}>
                    <label className="fs-status-filter">
                      <span>Status Filter</span>
                      <select
                        value={statusFilter}
                        onChange={(e) =>
                          setStatusFilter((e.target as HTMLSelectElement).value)
                        }
                      >
                        {statusOptions.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </td>
                  <td className="muted">10</td>
                </tr>
              </tbody>
            </table>
          </section>
        </div>

        <div className="modal-panel__foot">
          {msg ? (
            <span className="muted">{msg}</span>
          ) : (
            <span className="muted">{selectedCount} columns selected</span>
          )}
          <div style={{ flex: '1' }} />
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn--primary" onClick={() => void onSave()}>
            Save
          </button>
        </div>
      </div>
    </div>
  )
}
